import {Component, Injector, OnInit, ViewChild} from '@angular/core';
import {DateRange} from "@angular/material/datepicker";
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {CrudRetailerPipeline, RetailerPipeline} from "@features/leads/pipeline/pipeline.model";
import {Retailer} from "@features/leads/retailers/retailers.model";
import {LeaseBookingsComponent} from "@features/leasing/lease-bookings/lease-bookings.component";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {PageRequest} from "@ui-kit/framework/common/generic.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {
  CustomDateRangeChangeEventModel
} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range-change-event.model";
import {CustomDateRangeModel, TimeFrames} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {ChartConfiguration, ChartData} from "chart.js";
import {BaseChartDirective} from "ng2-charts";
import {lastValueFrom} from "rxjs";
import {GenericChart} from "src/app/shared/model/generic-chart.interface";
import {chartColors} from "../../../dashboards/chart-options/chart-colors.model";
import {SelectableCategory} from "../../../settings/industries/industry.model";
import {ManageIndustriesService} from "../../../settings/industries/manage-industries.service";
import {PipelineViewComponent} from "../pipeline-view/pipeline-view.component";
import {PipelineService} from "../pipeline.service";
import {barChartOptions, barChartPlugins} from "./chart-config.constant";

@Component({
  selector: 'app-pipeline-list',
  templateUrl: './pipeline-list.component.html',
  styleUrls: ['./pipeline-list.component.scss']
})
export class PipelineListComponent extends BasePageComponent implements OnInit {
  @ViewChild(BaseChartDirective) chart: BaseChartDirective | undefined;

  dateRange: DateRange<Date> = new DateRange<Date>(null, null);
  dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('all_time', 0, TimeFrames.DATE_RANGE, 'All time', false, new DateRange<Date>(new Date(this.offering!.offeringStartDate!), new Date())),
    new CustomDateRangeModel('this_month', 0, TimeFrames.MONTH, 'This month'),
    new CustomDateRangeModel('last_month', 1, TimeFrames.MONTH, 'Last month', true),
  ]

  pipelineData: CrudRetailerPipeline[] = [];

  allIndustries: SelectableCategory[] = [];

  // @ts-ignore
  barChartOptions: ChartConfiguration['options'] = barChartOptions;
  barChartPlugins = barChartPlugins;
  chartData: ChartData<'bar'> = {
    labels: [],
    datasets: [
      {data: []}
    ]
  }

  pipelineChartLoading: boolean = false;

  approvedRetailers: number = 0;
  rejectedRetailers: number = 0;

  protected readonly CrudRetailer = CrudRetailerPipeline;

  constructor(injector: Injector,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              private pipelineService: PipelineService,
              public loader: LoadingService,
              private industriesService: ManageIndustriesService,) {
    super(injector);
  }

  ngOnInit() {
    this.loadPipeline();
    this.loadIndustries();
  }

  loadPipeline() {
    const page: PageRequest = {
      size: '2000',
      page: '0',
      sort: ''
    };

    const filters = [
      {name: 'fromDate', val: this.dateUtils.displayShortDate(this.dateRange?.start)},
      {name: 'toDate', val: this.dateRange.end ? this.dateUtils.displayShortDate(this.dateRange.end) : null}
    ]

    this.pipelineService.getAll('/qualified-applicants', page, filters).subscribe({
      next: (data: {content: RetailerPipeline[]}) => {
        this.pipelineData = data.content.map(m => new CrudRetailerPipeline(m));
      },
      error: error => {
        console.log(error);
      }
    })
  }

  loadChartData() {
    this.pipelineChartLoading = true;
    const filters = [
      {name: 'fromDate', val: this.dateUtils.displayShortDate(this.dateRange?.start)},
      {name: 'toDate', val: this.dateRange.end ? this.dateUtils.displayShortDate(this.dateRange.end) : null}
    ]
    this.pipelineService.getChartData(filters).subscribe({
      next: (value: GenericChart) => {
        const industryData = value.chartSeries[0].chartData;
        this.approvedRetailers = Object(value.params)['approvedRetailers'];
        this.rejectedRetailers = Object(value.params)['rejectedRetailers'];
        this.chartData = {
          labels: industryData.map(m => this.getLabels(this.getIndustryCategoryName(m.label))),
          datasets: [
            {data: industryData.map(m => m.data), backgroundColor: chartColors.BLUE, borderColor: chartColors.BLUE, borderRadius: 5},
          ]
        }
        this.pipelineChartLoading = false;
      },
      error: err => {
        console.log(err);
        this.pipelineChartLoading = false;
      }
    })
  }

  loadIndustries() {
    lastValueFrom(this.industriesService.getAllSelectableOptionsForOffering())
      .then((res: SelectableCategory[]) => {
        this.allIndustries = res;
        this.loadChartData();
      });
  }

  retailerSelectedForView($event: any): void {
    if ($event) {
      const selectedRetailer = ($event as Retailer);
      this.openPipelineViewTab(selectedRetailer.id!.toString());
    }
  }

  openPipelineViewTab(id: string) {
    let payload: PageEventInterface = {
      componentToRegister: PipelineViewComponent,
      pageName: 'Pipeline ' + id,
      pageHeader: this.offering!.label,
      data: { id: id },
      id: this.utils.generateUuidWithPrefix('leads'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leads',
      pageCode: 'leads',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  getDateRange(event: CustomDateRangeChangeEventModel) {
    this.dateRange = event.dateRange;
    this.loadPipeline();
    this.loadChartData();
  }

  // chart

  getLabels(label: string) {
    let arr = [];
    let str = '';
    label.split(' ').forEach(x => {
      if (str !== '' && (str + ' ' + x).length > 10) {
        arr.push(str);
        str = '';
      }
      str = (str + ' ' + x).trim();
    });

    if (str !== '') {
      arr.push(str);
    }
    return arr;
  }

  getIndustryCategoryName(code: string) {
    const industryCat = this.allIndustries.filter(f => f.code === code)[0];
    return industryCat.labelNameOverride ?? industryCat.name;
  }

  openCreateLeaseTab() {
    let payload: PageEventInterface = {
      componentToRegister: LeaseBookingsComponent,
      pageName: 'Create a lease',
      pageHeader: this.offering!.label,
      data: {},
      id: this.utils.generateUuidWithPrefix('lease-bookings'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-bookings',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }
}
